import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { ProposedByPerson, ProposedSummary } from '../../types';
import { colors } from '../../styles/theme';
import styles from './CandidateDealsByPerson.module.css';

/**
 * Candidate (D8 proposed) deals, one row per person, split by how long the candidate has sat
 * unreviewed. Shown only under the page's "Include AI-proposed deals" lens.
 *
 * The card reads "AI Proposed" to the user since ovw-2 (owner request 2026-09-20); the component,
 * its file and every field it consumes keep the `candidate` / `proposed` names they were built
 * with, so the label change moves no code.
 *
 * Two things about this card that are NOT obvious from the picture:
 *
 *  - **"Person" is the candidate's read audience** — whoever can currently see and accept/reject it
 *    (owner ruling D2). It is read from the live entitlement table, never re-derived from evidence
 *    or the projected deal team, so the chart cannot disagree with who actually sees the row. A
 *    candidate visible to three people therefore counts for each of the three, which is why the
 *    caption says so rather than leaving the reader to sum the bars and get more than the total.
 *  - **Counts only.** No name, codename, stage, id or value for a candidate reaches this page
 *    (owner ruling D1); the payload carries numbers and a person label, nothing else.
 *
 * The age anchor is the proposed entry's `created_at` — candidate birth — and the bucketing is the
 * builder's (spec §3.7), so nothing here does date maths.
 */

// Only the SERIES colours are identity, and those come from the theme. The axis/tooltip greys below
// and the card chrome in the stylesheet are copied verbatim from the sibling analytics cards
// (`PipelineByTeam`, `PipelineBySector`) so this card is indistinguishable from them; they are the
// page's existing literals, not a new palette. Re-toning all of them belongs to one deliberate pass
// over `components/analytics/*`, not to this feature.
const AGE_SERIES = [
  { key: 'fresh', label: 'Fresh (≤1 wk)', fill: colors.candidate.fresh },
  { key: 'aging', label: 'Aging (1–4 wks)', fill: colors.candidate.aging },
  { key: 'stale', label: 'Stale (>4 wks)', fill: colors.candidate.stale },
] as const;

/** The disclosure the flag discussion in spec §4b leaves as the card's only residual. */
function caption(summary: ProposedSummary): string {
  const base =
    `${summary.count} AI-proposed deal${summary.count === 1 ? '' : 's'} · counted for the people ` +
    'who can currently see and act on each one; a deal with several people counts for each';
  return summary.no_audience > 0
    ? `${base} · ${summary.no_audience} not yet attributed to anyone`
    : base;
}

interface Props {
  /** Already sorted `(-total, person_name)` by the builder — rendered in payload order, never
   *  re-sorted here, so the chart's order is the one the backend's tests pin. */
  data: ProposedByPerson[];
  summary?: ProposedSummary | null;
}

export function CandidateDealsByPerson({ data, summary }: Props) {
  const header = (
    <div className={styles.headerBar}>
      <span className={styles.headerLabel}>AI Proposed Deals by Person</span>
      <span className={styles.headerSub}>Time unreviewed</span>
    </div>
  );

  if (data.length === 0) {
    return (
      <div className={styles.card}>
        {header}
        <div className={styles.empty}>No AI-proposed deals are attributed to anyone yet</div>
        {summary && summary.count > 0 && <div className={styles.caption}>{caption(summary)}</div>}
      </div>
    );
  }

  const chartData = data.map((d) => ({ ...d, name: d.person_name }));

  return (
    <div className={styles.card}>
      {header}
      <div className={styles.body}>
        <ResponsiveContainer width="100%" height={Math.max(200, data.length * 36)}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
          >
            <XAxis
              type="number"
              tick={{ fontSize: 10, fontFamily: "'DM Mono', monospace", fill: '#999' }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif", fill: '#1A1A1A' }}
              width={140}
            />
            <Tooltip
              contentStyle={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #E5E5E0',
              }}
            />
            <Legend
              wrapperStyle={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                textTransform: 'uppercase',
              }}
            />
            {AGE_SERIES.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stackId="a"
                fill={s.fill}
                radius={i === AGE_SERIES.length - 1 ? [0, 3, 3, 0] : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {summary && <div className={styles.caption}>{caption(summary)}</div>}
    </div>
  );
}
